/**
 * What the TV header bar says: how old the data is, how far the page has
 * run, and the time in the office.
 */

/** Data older than this many refresh cycles is flagged as stale. */
export const DISPLAY_STALE_CYCLES = 3

/** One query the bar may read the data age from. */
export interface HeaderQuery {
  /** When the query last answered, in epoch milliseconds. 0 means never. */
  dataUpdatedAt: number
  /** Whether a mounted component reads the query. */
  observed: boolean
  /** Whether the query is quiet: settings, a catalogue, a saved document. */
  quiet: boolean
}

/**
 * The newest answer among the queries the page reads from the datalake.
 *
 * A quiet query refreshes on its own schedule and says nothing about the
 * report's data, so it does not count. Nor does a query that no mounted
 * component reads: it belongs to the page before.
 *
 * @param queries - Every query in the cache.
 * @returns The newest answer time, or null when the page has none yet.
 */
export function newestUpdate(queries: readonly HeaderQuery[]): number | null {
  let newest: number | null = null
  for (const query of queries) {
    if (query.observed && !query.quiet && query.dataUpdatedAt > 0) {
      newest = Math.max(newest ?? 0, query.dataUpdatedAt)
    }
  }

  return newest
}

/**
 * The oldest answer among the queries the page reads from the datalake.
 *
 * The stale flag reads this one, so one fresh query cannot hide a stale one.
 *
 * @param queries - Every query in the cache.
 * @returns The oldest answer time, or null when the page has none yet.
 */
export function oldestUpdate(queries: readonly HeaderQuery[]): number | null {
  let oldest: number | null = null
  for (const query of queries) {
    if (query.observed && !query.quiet && query.dataUpdatedAt > 0) {
      oldest = Math.min(oldest ?? Number.POSITIVE_INFINITY, query.dataUpdatedAt)
    }
  }

  return oldest
}

/**
 * The data age, as the bar prints it.
 *
 * @param updatedAt - The newest answer time, in epoch milliseconds.
 * @param now - The clock reading, in epoch milliseconds.
 * @returns "Updated just now", "Updated 4 min ago" or "Updated 2 h ago".
 */
export function updatedLabel(updatedAt: number, now: number): string {
  const minutes = Math.floor(Math.max(0, now - updatedAt) / 60_000)
  if (minutes < 1) return 'Updated just now'
  if (minutes < 60) return `Updated ${minutes} min ago`

  return `Updated ${Math.floor(minutes / 60)} h ago`
}

/**
 * Whether the data is older than the stale limit.
 *
 * @param updatedAt - The newest answer time, in epoch milliseconds.
 * @param now - The clock reading, in epoch milliseconds.
 * @param refreshMs - How often the display refetches, in milliseconds.
 * @returns True when the data missed more than the allowed refresh cycles.
 */
export function isStale(
  updatedAt: number,
  now: number,
  refreshMs: number
): boolean {
  return now - updatedAt > DISPLAY_STALE_CYCLES * refreshMs
}

/**
 * How much of the page's time has run.
 *
 * @param shownAt - When the page went up, in epoch milliseconds.
 * @param seconds - How long the page stays up.
 * @param now - The clock reading, in epoch milliseconds.
 * @returns A share from 0 to 1.
 */
export function progressShare(
  shownAt: number,
  seconds: number,
  now: number
): number {
  if (!(seconds > 0)) return 1

  return Math.min(1, Math.max(0, (now - shownAt) / (seconds * 1000)))
}

/**
 * The office time, as the bar prints it.
 *
 * @param now - The clock reading, in epoch milliseconds.
 * @param timeZone - The zone the display resolves.
 * @returns The weekday and the time, such as "Mon 3:42 PM".
 */
export function clockLabel(now: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit'
  }).format(now)
}
